import type { ContributorDto, RoleSlotDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import {
  filterRoleSlotsForInvitePublication,
  type InvitePublicationUiContext,
} from '@/utils/contributor-invite-publication.utils';

/** Same visibility rules as contributor-roles slot list (flow context + library + ORGANIZE publication). */
export function filterSlotsForStagingInviteBuild(
  slots: RoleSlotDto[] | undefined,
  flowContext: FlowDto | undefined,
  role?: ContributorDto['roles'][0],
  invitePublicationCtx?: InvitePublicationUiContext,
): RoleSlotDto[] {
  if (!slots?.length) return [];
  const base = slots.filter((slot) => {
    if (slot.deleted === true) return false;
    if (slot.entityType === 'FlowSlot') {
      const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity.id;
      if (flowContext) {
        const matchesFlowSlot = flowContext.slots?.some((flowSlot) => flowSlot.id === entityId);
        if (!matchesFlowSlot) return false;
        if (flowContext.slot?.id && slot.eventSlotId) {
          return slot.eventSlotId === flowContext.slot.id || slot.eventSlotId === flowContext.slot.origin;
        }
        return true;
      }
      return !flowContext;
    }
    if (slot.entityType === 'Flow') {
      if (!flowContext) return true;
      if (flowContext.slot?.id && slot.eventSlotId) {
        return slot.eventSlotId === flowContext.slot.id || slot.eventSlotId === flowContext.slot.origin;
      }
      return true;
    }
    return !flowContext;
  });
  if (!role || !invitePublicationCtx) {
    return base;
  }
  return filterRoleSlotsForInvitePublication(role, base, invitePublicationCtx);
}

/**
 * Raw staged flags on non-deleted roles/slots only.
 * Do not use `contributor.stagedForInvite` alone — it mirrors children and can stay true after slots are soft-deleted.
 */
export function contributorHasAnyStagedForInvite(contributor: ContributorDto): boolean {
  return contributor.roles.some((r) => {
    if ((r as { deleted?: boolean }).deleted) return false;
    if (r.stagedForInvite === true) return true;
    return r.slots?.some((s) => s.deleted !== true && s.stagedForInvite === true) ?? false;
  });
}

/**
 * Build POST /invitations/event/.../contributors payload roles for one contributor from staged flags.
 */
export function buildStagedInviteRolesForContributor(
  contributor: ContributorDto,
  flowContext: FlowDto | undefined,
  invitePublicationCtx?: InvitePublicationUiContext,
): { roleId: string; slotIds: string[] }[] {
  const rolesOut: { roleId: string; slotIds: string[] }[] = [];

  for (const role of contributor.roles) {
    if ((role as { deleted?: boolean }).deleted) continue;

    const visibleSlots = filterSlotsForStagingInviteBuild(role.slots, flowContext, role, invitePublicationCtx);
    /** Staged slots that still need an invite (exclude already invited+accepted). */
    const stagedSlotsNeedingInvite = visibleSlots.filter(
      (s) => s.stagedForInvite === true && !(!!s.invitedAt && s.acceptedAt),
    );
    const stagedSlotIds = stagedSlotsNeedingInvite.map((s) => s.id);
    /** Role checkbox staged but role-level invite is already complete — do not emit empty slotIds-only “re-invite”. */
    const roleLevelStagedNeedsInvite = role.stagedForInvite === true && !(!!role.invitedAt && role.acceptedAt);

    // Do not use contributor.stagedForInvite: it is set whenever any role/slot is staged (mirror), not “all roles”.
    // Must match buildInviteModalPreview so the POST payload matches the confirmation modal.
    const includeRole = roleLevelStagedNeedsInvite || stagedSlotIds.length > 0;
    if (!includeRole) continue;

    if (stagedSlotIds.length > 0) {
      if (!contributor.invitedAt) {
        rolesOut.push({ roleId: role.id, slotIds: [] });
      }
      rolesOut.push({ roleId: role.id, slotIds: stagedSlotIds });
    } else {
      rolesOut.push({ roleId: role.id, slotIds: [] });
    }
  }

  return rolesOut;
}

/** True iff the staged selection would produce a non-empty invite payload (matches modal / POST build). */
export function contributorHasInviteableStagedSelection(
  contributor: ContributorDto,
  flowContext: FlowDto | undefined,
  invitePublicationCtx?: InvitePublicationUiContext,
): boolean {
  return buildStagedInviteRolesForContributor(contributor, flowContext, invitePublicationCtx).length > 0;
}

export function buildStagedContributorsInvitationPayload(
  contributors: ContributorDto[],
  flowContext: FlowDto | undefined,
  options?: { contributorIds?: string[]; invitePublicationCtx?: InvitePublicationUiContext },
): { contributorId: string; roles: { roleId: string; slotIds: string[] }[] }[] {
  const idFilter = options?.contributorIds ? new Set(options.contributorIds) : null;
  const invitePublicationCtx = options?.invitePublicationCtx;

  return contributors
    .filter((c) => {
      if (idFilter && !idFilter.has(c.id)) return false;
      return contributorHasInviteableStagedSelection(c, flowContext, invitePublicationCtx);
    })
    .map((c) => ({
      contributorId: c.id,
      roles: buildStagedInviteRolesForContributor(c, flowContext, invitePublicationCtx),
    }))
    .filter((entry) => entry.roles.length > 0);
}

export type StagedInviteSkip = { contributorId: string; displayName: string; reasons: string[] };

export function computeStagedInviteSkips(
  contributors: ContributorDto[],
  flowContext: FlowDto | undefined,
  options?: { contributorIds?: string[]; invitePublicationCtx?: InvitePublicationUiContext },
): StagedInviteSkip[] {
  const idFilter = options?.contributorIds ? new Set(options.contributorIds) : null;
  const invitePublicationCtx = options?.invitePublicationCtx;
  const skips: StagedInviteSkip[] = [];

  for (const c of contributors) {
    if (idFilter && !idFilter.has(c.id)) continue;
    if (!contributorHasInviteableStagedSelection(c, flowContext, invitePublicationCtx)) continue;

    const displayName = c.profiles?.[0]?.displayName?.trim() || c.email || c.id;
    const reasons: string[] = [];
    if (!c.email?.trim()) {
      reasons.push('missingEmail');
    }
    const roles = buildStagedInviteRolesForContributor(c, flowContext, invitePublicationCtx);
    if (roles.length === 0) {
      reasons.push('noInviteableSelection');
    }
    if (reasons.length > 0) {
      skips.push({ contributorId: c.id, displayName, reasons });
    }
  }

  return skips;
}
